import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Sparkles, Upload, FileText, Loader2 } from "lucide-react";
import { formatApiError } from "../lib/api";
import PhoneNumberInput from "../components/PhoneNumberInput";
import AvatarUpload from "../components/AvatarUpload";
import SkillsAutocomplete from "../components/SkillsAutocomplete";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { useAuth } from "../contexts/AuthContext";

const LINKEDIN_RE = /^https?:\/\/([\w-]+\.)*linkedin\.com\//i;
const GITHUB_RE = /^https?:\/\/([\w-]+\.)*github\.com\//i;
import ChangePasswordCard from "../components/ChangePasswordCard";

const ROLES = [
  "Manual QA Engineer", "Automation QA Engineer", "SDET", "Performance Test Engineer",
  "QA Lead", "QA Architect", "Mobile Test Engineer", "API Test Engineer", "Security QA",
];

export default function SeekerProfile() {
  const [params] = useSearchParams();
  const initialTab = params.get("tab") === "skill-gap" ? "skill-gap" : "profile";
  const { user } = useAuth();
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    headline: "", summary: "", location: "", years_experience: 0,
    skills: [], tools: [], certifications: [], target_role: "",
    expected_salary: "", salary_currency: "INR", linkedin: "", github: "",
    phone: "",
  });
  const [uploading, setUploading] = useState(false);
  const [resumeFilename, setResumeFilename] = useState("");
  const fileRef = useRef();

  // Skill gap state
  const [gap, setGap] = useState(null);
  const [gapRunning, setGapRunning] = useState(false);
  useDocumentTitle("My profile");

  useEffect(() => {
    api.get("/profile/seeker")
      .then(({ data }) => {
        if (data) {
          setProfile({
            headline: data.headline || "",
            summary: data.summary || "",
            location: data.location || "",
            years_experience: data.years_experience || 0,
            skills: data.skills || [],
            tools: data.tools || [],
            certifications: data.certifications || [],
            target_role: data.target_role || "",
            expected_salary: data.expected_salary || "",
            salary_currency: data.salary_currency || "INR",
            linkedin: data.linkedin || "",
            github: data.github || "",
            phone: data.phone || "",
          });
          setResumeFilename(data.resume_filename || "");
          if (data.last_skill_gap) setGap(data.last_skill_gap);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    // Client-side URL validation (mirrors backend)
    if (profile.linkedin && !LINKEDIN_RE.test(profile.linkedin.trim())) {
      toast.error("Please specify a valid LinkedIn profile link.");
      return;
    }
    if (profile.github && !GITHUB_RE.test(profile.github.trim())) {
      toast.error("Please specify a valid GitHub link.");
      return;
    }
    // Phone: if the user typed digits but the value isn't the canonical
    // "+<code> <digits>" shape, it's incomplete.
    if (profile.phone) {
      const m = profile.phone.match(/^\+(\d{1,4})\s+(\d{6,15})$/);
      if (!m) {
        toast.error("Enter a valid phone number (up to 10 digits).");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { ...profile, years_experience: Number(profile.years_experience) || 0 };
      await api.put("/profile/seeker", payload);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadResume = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/resume/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResumeFilename(data.filename);
      toast.success("Resume uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const runSkillGap = async () => {
    if (!profile.target_role) {
      toast.error("Set a target role first");
      return;
    }
    setGapRunning(true);
    try {
      const { data } = await api.post("/ai/skill-gap", {
        target_role: profile.target_role,
        current_skills: profile.skills,
        years_experience: Number(profile.years_experience) || 0,
      });
      setGap(data);
      toast.success("Analysis complete");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Analysis failed");
    } finally {
      setGapRunning(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen"><Header /><div className="p-10 text-sm text-zinc-500">Loading...</div></div>
  );

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-1">your profile</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-zinc-950">Build your tester profile</h1>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid w-full sm:w-fit grid-cols-2">
            <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
            <TabsTrigger value="skill-gap" data-testid="tab-skill-gap">AI Skill Gap</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-6 space-y-6">
            <Card className="p-6 space-y-4">
              <h2 className="font-display text-lg font-bold tracking-tight">Profile picture</h2>
              <AvatarUpload userId={user?.user_id} fallback={user?.name || user?.email} />
            </Card>
            <Card className="p-6 space-y-4">
              <h2 className="font-display text-lg font-bold tracking-tight">Basics</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Headline" v={profile.headline} on={(v)=>setProfile(p=>({...p, headline: v}))} placeholder="SDET specialising in Playwright + Java" testid="input-headline" />
                <Field label="Location" v={profile.location} on={(v)=>setProfile(p=>({...p, location: v}))} placeholder="Bengaluru, IN" testid="input-location" />
                <div>
                  <Label>Target role</Label>
                  <select
                    value={profile.target_role}
                    onChange={(e)=>setProfile(p=>({...p, target_role: e.target.value}))}
                    data-testid="input-target-role"
                    className="mt-1.5 w-full h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20"
                  >
                    <option value="">Select…</option>
                    {ROLES.map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <Field label="Years experience" type="number" v={profile.years_experience} on={(v)=>setProfile(p=>({...p, years_experience: v}))} testid="input-years" />
                <div>
                  <Label>Expected salary</Label>
                  <div className="mt-1.5 flex gap-2">
                    <select
                      value={profile.salary_currency}
                      onChange={(e)=>setProfile(p=>({...p, salary_currency: e.target.value}))}
                      data-testid="input-salary-currency"
                      className="w-24 h-10 rounded-md border border-zinc-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20"
                    >
                      <option value="INR">₹ INR</option>
                      <option value="USD">$ USD</option>
                      <option value="EUR">€ EUR</option>
                      <option value="GBP">£ GBP</option>
                      <option value="AUD">A$ AUD</option>
                      <option value="SGD">S$ SGD</option>
                      <option value="AED">د.إ AED</option>
                      <option value="CAD">C$ CAD</option>
                    </select>
                    <Input
                      value={profile.expected_salary}
                      onChange={(e)=>setProfile(p=>({...p, expected_salary: e.target.value}))}
                      placeholder={profile.salary_currency === "INR" ? "e.g. 12L – 18L per annum" : "e.g. 80k – 100k"}
                      data-testid="input-salary"
                      className="flex-1"
                    />
                  </div>
                </div>
                <Field label="LinkedIn" v={profile.linkedin} on={(v)=>setProfile(p=>({...p, linkedin: v}))} placeholder="https://linkedin.com/in/..." testid="input-linkedin" />
                <Field label="GitHub" v={profile.github} on={(v)=>setProfile(p=>({...p, github: v}))} placeholder="https://github.com/..." testid="input-github" />
                <div className="md:col-span-2">
                  <Label>Phone number</Label>
                  <PhoneNumberInput
                    value={profile.phone}
                    onChange={(v) => setProfile((p) => ({ ...p, phone: v }))}
                    testIdPrefix="input-phone"
                    className="mt-1.5"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    Only shared with <span className="font-semibold">verified employers</span> so they can reach you directly. Digits only — max 10 numbers.
                  </p>
                </div>
              </div>
              <div>
                <Label>About / Summary</Label>
                <Textarea
                  value={profile.summary}
                  onChange={(e)=>setProfile(p=>({...p, summary: e.target.value}))}
                  rows={4}
                  placeholder="What kinds of testing do you love? What was your biggest QA win?"
                  data-testid="input-summary"
                  className="mt-1.5"
                />
              </div>
            </Card>

            <Card className="p-6 space-y-6">
              <div>
                <h2 className="font-display text-lg font-bold tracking-tight">Skills</h2>
                <p className="text-xs text-zinc-500 mt-1 mb-3">
                  Start typing (min 3 characters). We only accept skills from our QA/testing catalog so recruiters can match you accurately.
                </p>
                <SkillsAutocomplete
                  values={profile.skills}
                  onChange={(next) => setProfile((p) => ({ ...p, skills: next }))}
                  placeholder="e.g. Selenium, Playwright, k6…"
                  testidPrefix="skill"
                />
              </div>

              <div>
                <h2 className="font-display text-lg font-bold tracking-tight">Tools & platforms</h2>
                <p className="text-xs text-zinc-500 mt-1 mb-3">
                  Frameworks, CI/CD, cloud, test-management tools — same catalog, min 3 characters to search.
                </p>
                <SkillsAutocomplete
                  values={profile.tools}
                  onChange={(next) => setProfile((p) => ({ ...p, tools: next }))}
                  placeholder="e.g. Jenkins, Postman, TestRail…"
                  testidPrefix="tool"
                />
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="font-display text-lg font-bold tracking-tight mb-3">Resume</h2>
              {resumeFilename ? (
                <div className="flex items-center justify-between p-3 border border-zinc-200 rounded-md bg-white">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-[#002FA7]" />
                    <div>
                      <div className="font-medium text-zinc-950">{resumeFilename}</div>
                      <div className="text-xs text-zinc-500">Uploaded</div>
                    </div>
                  </div>
                  <Button variant="outline" onClick={()=>fileRef.current?.click()} data-testid="replace-resume-btn">
                    Replace
                  </Button>
                </div>
              ) : (
                <button
                  onClick={()=>fileRef.current?.click()}
                  data-testid="upload-resume-btn"
                  className="w-full border-2 border-dashed border-zinc-300 rounded-md p-8 hover:border-[#002FA7] hover:bg-blue-50/30 transition-colors text-center"
                >
                  <Upload className="h-6 w-6 mx-auto text-zinc-400 mb-2" />
                  <div className="text-sm font-medium text-zinc-700">Click to upload your resume</div>
                  <div className="text-xs text-zinc-500 mt-1">PDF, DOC, or DOCX (max 10 MB)</div>
                </button>
              )}
              <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" hidden onChange={uploadResume} data-testid="resume-file-input" />
              {uploading && <div className="text-xs text-zinc-500 mt-2 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin"/> Uploading…</div>}
            </Card>

            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} data-testid="save-profile-btn" className="bg-[#002FA7] hover:bg-[#00227A] text-white">
                {saving ? "Saving..." : "Save profile"}
              </Button>
            </div>

            <div className="mt-4">
              <div className="mb-3 text-xs uppercase tracking-widest text-zinc-500 font-bold tag-mono">account security</div>
              <ChangePasswordCard />
            </div>
          </TabsContent>

          <TabsContent value="skill-gap" className="mt-6 space-y-6">
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-5 w-5 text-[#002FA7]" />
                <h2 className="font-display text-xl font-bold tracking-tight">AI Skill Gap Analyzer</h2>
              </div>
              <p className="text-sm text-zinc-600 mb-4">
                Powered by Claude Sonnet 4.5. We'll analyze your current skills and your target role,
                then return a personalized match score and a step-by-step learning path.
              </p>

              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div>
                  <Label>Target role</Label>
                  <select
                    value={profile.target_role}
                    onChange={(e)=>setProfile(p=>({...p, target_role: e.target.value}))}
                    className="mt-1.5 w-full h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                    data-testid="gap-target-role"
                  >
                    <option value="">Select…</option>
                    {ROLES.map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <Field label="Years experience" type="number" v={profile.years_experience} on={(v)=>setProfile(p=>({...p, years_experience: v}))} testid="gap-years" />
                <div className="flex items-end">
                  <Button
                    onClick={runSkillGap}
                    disabled={gapRunning}
                    data-testid="run-gap-btn"
                    className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white"
                  >
                    {gapRunning ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/> Analyzing…</> : <>Run analysis</>}
                  </Button>
                </div>
              </div>

              <div className="text-xs text-zinc-500">Using {profile.skills.length} skill(s) from your profile.</div>
            </Card>

            {gap && (
              <Card className="p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold">match score</div>
                    <div className="font-display text-5xl font-black tracking-tighter text-zinc-950 mt-1">{gap.match_score}<span className="text-2xl text-zinc-400">/100</span></div>
                  </div>
                  <span className="ai-score-badge">claude-sonnet-4.5</span>
                </div>
                <p className="text-zinc-700">{gap.summary}</p>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold mb-2">your strengths</div>
                    <ul className="space-y-1 text-sm">
                      {(gap.strengths || []).map((s, i) => (
                        <li key={`str-${i}-${String(s).slice(0, 40)}`} className="text-zinc-700">• {s}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-amber-700 font-bold mb-2">skills to add</div>
                    <ul className="space-y-2 text-sm">
                      {(gap.missing_skills || []).map((m, i) => {
                        const skill = typeof m === "string" ? m : m.skill;
                        const priority = typeof m === "object" ? m.priority : null;
                        const why = typeof m === "object" ? m.why : null;
                        return (
                          <li key={`gap-${skill || i}`} className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-2">
                              <span className="tag-mono font-semibold text-zinc-950">{skill}</span>
                              {priority && (
                                <Badge className={
                                  priority === "high" ? "bg-red-50 text-red-700 border border-red-200" :
                                  priority === "medium" ? "bg-amber-50 text-amber-700 border border-amber-200" :
                                  "bg-zinc-50 text-zinc-700 border border-zinc-200"
                                }>{priority}</Badge>
                              )}
                            </div>
                            {why && <span className="text-zinc-600 text-xs">{why}</span>}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-2">learning path</div>
                  <ol className="space-y-2 text-sm list-decimal list-inside">
                    {(gap.learning_path || []).map((s, i) => (
                      <li key={`lp-${i}-${String(s).slice(0, 40)}`} className="text-zinc-700">{s}</li>
                    ))}
                  </ol>
                </div>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function Field({ label, v, on, placeholder, type="text", testid }) {
  const isNumber = type === "number";
  const handleChange = (e) => {
    if (isNumber) {
      const raw = String(e.target.value).replace(/[^\d]/g, "");
      // strip leading zeros (but allow "0")
      const cleaned = raw === "" ? "" : String(parseInt(raw, 10));
      on(cleaned);
    } else {
      on(e.target.value);
    }
  };
  const handleKeyDown = (e) => {
    if (isNumber && ["-", "e", "E", "+", "."].includes(e.key)) e.preventDefault();
  };
  return (
    <div>
      <Label>{label}</Label>
      <Input
        value={v}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        type={type}
        min={isNumber ? "0" : undefined}
        max={isNumber ? "60" : undefined}
        inputMode={isNumber ? "numeric" : undefined}
        placeholder={placeholder}
        className="mt-1.5"
        data-testid={testid}
      />
    </div>
  );
}
